// Import react elements
import { useState, useEffect, useRef } from "react";

// Import utilities
import { ToTitleCase } from "global/utilities/ConvertCasing"; 

// Import css
import "./ImageCarouselPopup.css";



// Component to display a full-screen image carousel popup
const ImageCarouselPopup = ({ images = [], initialImage, onClose }) => {


    // State to track the currently displayed image index
    const [currentIndex, setCurrentIndex] = useState(0);

    // Ref to the thumbnail scroller DOM element
    const thumbnailRef = useRef();

    // Refs to track swipe start and end positions
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);



    // Set the initial image index when the popup opens
    useEffect(() => {

        // Find the index of the initial image by matching any possible image URL field
        const startIndex = images.findIndex((img) => (
            img.url === initialImage ||
            img.image_url === initialImage ||
            img.mockup_image_url === initialImage
        ));

        // Set index to found image or default to the first image
        setCurrentIndex(startIndex >= 0 ? startIndex : 0);

    }, [initialImage, images]);



    // Move to the next image in the carousel
    const nextImage = () => {

        setCurrentIndex((prev) => (prev + 1) % images.length);

    };



    // Move to the previous image in the carousel
    const prevImage = () => {

        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

    };



    // Scroll thumbnail row left or right
    const scrollThumbnails = (direction) => {

        if (thumbnailRef.current) {

            thumbnailRef.current.scrollBy({

                left: direction === "left" ? -100 : 100,

                behavior: "smooth",

            });

        }

    };



    // Set up keyboard navigation and close popup with Escape
    useEffect(() => {

        const handleKeyDown = (e) => {

            if (e.key === "ArrowLeft") prevImage();
            if (e.key === "ArrowRight") nextImage();
            if (e.key === "Escape") onClose();

        };

        // Add keydown listener
        window.addEventListener("keydown", handleKeyDown);

        // Cleanup on unmount
        return () => window.removeEventListener("keydown", handleKeyDown);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClose]);



    // Capture touch start position for swipe
    const handleTouchStart = (e) => {

        touchStartX.current = e.changedTouches[0].clientX;

    };



    // Capture touch end position and trigger swipe handler
    const handleTouchEnd = (e) => {

        touchEndX.current = e.changedTouches[0].clientX;

        handleSwipe();

    };



    // Determine swipe direction and navigate carousel
    const handleSwipe = () => {

        const deltaX = touchStartX.current - touchEndX.current;

        if (Math.abs(deltaX) > 50) {

            deltaX > 0 ? nextImage() : prevImage();

        }

    };



    // Don't render anything if there are no images
    if (!images.length) return null;



    return (

        // Overlay container that closes the popup when clicked
        <div className="carousel-popup-overlay" onClick={onClose}>

            {/* Stop propagation to prevent closing when clicking inside the content */}
            <div className="carousel-popup-content" onClick={(e) => e.stopPropagation()}>

                <div className="carousel-popup-container">

                    {/* Close button (X) */}
                    <button type="button" className="carousel-close" onClick={onClose}>×</button>

                    {/* Main image area */}
                    <div className="carousel-main">

                        {/* Show left nav button if more than one image */}
                        {images.length > 1 && (

                            <button type="button" className="carousel-nav left" onClick={prevImage}>❮</button>

                        )}

                        <div className="carousel-image-container">

                            <img
                                src={images[currentIndex].url || images[currentIndex].image_url || images[currentIndex].mockup_image_url }
                                alt={images[currentIndex].imageTitle || images[currentIndex].image_id || ""}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                            />

                            {images[currentIndex].imageTitle &&

                                <figcaption>{ToTitleCase(images[currentIndex].imageTitle)}</figcaption>
                            
                            }
                            
                        </div>

                        {/* Show right nav button if more than one image */}
                        {images.length > 1 && (

                            <button type="button" className="carousel-nav right" onClick={nextImage}>❯</button>

                        )}

                    </div>

                    {/* Thumbnail strip */}
                    <div className="carousel-thumbnails-wrapper">

                        {/* Scroll thumbnails left if more than one image */}
                        {images.length > 1 && (

                            <button type="button" onClick={() => scrollThumbnails("left")} className="thumb-scroll">◀</button>

                        )}

                        <div className="carousel-thumbnails" ref={thumbnailRef}>

                            {images.map((img, index) => (
                                <img
                                    key={index}
                                    src={img.url || img.image_url || img.mockup_image_url}
                                    alt={img.imageTitle || images[currentIndex].image_id || ""}
                                    className={`thumbnail ${index === currentIndex ? "active" : ""}`}
                                    onClick={() => setCurrentIndex(index)}
                                />
                            ))}

                        </div>

                        {/* Scroll thumbnails right if more than one image */}
                        {images.length > 1 && (

                            <button type="button" onClick={() => scrollThumbnails("right")} className="thumb-scroll">▶</button>

                        )}

                    </div>

                </div>

            </div>

        </div>

    );

};

export default ImageCarouselPopup;
